import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bell, BookOpen, Check, Film, Globe, Home, Menu, Moon, Printer, ShoppingCart, Sun, User, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useShop } from '../../store';
import { ensureLanguageLoaded, staticLanguageOptions } from '../../i18n';
import { notificationService, type NotificationRecord } from '../../services/notificationService';

interface NavItem {
  to: string;
  labelKey: string;
}

interface SiteHeaderProps {
  isHomePage: boolean;
  onOpenCart: () => void;
  overlayIntensity?: 'normal' | 'dark';
}

const getUserInitials = (name: string): string =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((chunk) => chunk.charAt(0).toUpperCase())
    .join('');

const getLanguageFlag = (languageCode: string): string => {
  switch (languageCode.trim().toLowerCase()) {
    case 'en':
      return '🇬🇧';
    case 'fr':
      return '🇫🇷';
    case 'rw':
      return '🇷🇼';
    case 'sw':
      return '🇹🇿';
    default:
      return '🌍';
  }
};

const SiteHeader = ({ isHomePage, onOpenCart }: SiteHeaderProps) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, cartTotalItems, logoutAsync, isLoading, themeMode, toggleTheme } = useShop();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const [isLanguageLoading, setIsLanguageLoading] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [notifications, setNotifications] = useState<NotificationRecord[]>([]);
  const [isNotificationsLoading, setIsNotificationsLoading] = useState(false);
  const [notificationsError, setNotificationsError] = useState('');
  const profileMenuRef = useRef<HTMLDivElement | null>(null);
  const desktopLanguageMenuRef = useRef<HTMLDivElement | null>(null);
  const mobileLanguageMenuRef = useRef<HTMLDivElement | null>(null);

  const currentLanguage = (i18n.resolvedLanguage ?? i18n.language).toLowerCase();
  const currentLanguageOption =
    staticLanguageOptions.find((option) => option.code === currentLanguage) ?? staticLanguageOptions[0];
  const dashboardPath =
    currentUser?.role === 'admin'
      ? '/admin/dashboard/overview'
      : currentUser?.role === 'manager'
        ? '/admin/dashboard/pod'
      : currentUser?.role === 'vendor'
        ? '/vendor/dashboard/overview'
        : currentUser?.role === 'buyer'
          ? '/dashboard/home'
          : null;
  const dashboardLabelKey = currentUser?.role === 'admin' ? 'nav.adminDashboard' : 'nav.dashboard';

  const changeLanguage = async (languageCode: string) => {
    const normalizedLanguageCode = languageCode.trim().toLowerCase();
    if (!normalizedLanguageCode) {
      return;
    }

    setIsLanguageLoading(true);
    try {
      await ensureLanguageLoaded(normalizedLanguageCode);
      await i18n.changeLanguage(normalizedLanguageCode);
    } finally {
      setIsLanguageLoading(false);
    }
  };

  const handleLanguageSelect = async (languageCode: string) => {
    setIsLanguageMenuOpen(false);
    await changeLanguage(languageCode);
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    await logoutAsync();
    setIsLoggingOut(false);
    setIsProfileMenuOpen(false);
    setMenuOpen(false);
    void navigate('/');
  };

  const handleOpenProfile = () => {
    setIsProfileMenuOpen(false);
    setMenuOpen(false);
    void navigate('/profile');
  };

  const handleOpenDashboard = () => {
    if (!dashboardPath) {
      return;
    }

    setIsProfileMenuOpen(false);
    setMenuOpen(false);
    void navigate(dashboardPath);
  };

  useEffect(() => {
    if (!isProfileMenuOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
    };

    window.addEventListener('mousedown', handleClickOutside);
    return () => window.removeEventListener('mousedown', handleClickOutside);
  }, [isProfileMenuOpen]);

  useEffect(() => {
    if (!isLanguageMenuOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedDesktopMenu = desktopLanguageMenuRef.current?.contains(target);
      const clickedMobileMenu = mobileLanguageMenuRef.current?.contains(target);
      if (!clickedDesktopMenu && !clickedMobileMenu) {
        setIsLanguageMenuOpen(false);
      }
    };

    window.addEventListener('mousedown', handleClickOutside);
    return () => window.removeEventListener('mousedown', handleClickOutside);
  }, [isLanguageMenuOpen]);

  useEffect(() => {
    if (!menuOpen) {
      setIsProfileMenuOpen(false);
      setIsLanguageMenuOpen(false);
    }
  }, [menuOpen]);

  useEffect(() => {
    if (!currentUser) {
      setIsProfileMenuOpen(false);
    }
  }, [currentUser]);

  useEffect(() => {
    setMenuOpen(false);
    setIsProfileMenuOpen(false);
    setIsNotificationsOpen(false);
    setIsLanguageMenuOpen(false);
  }, [location.pathname]);

  const loadNotifications = useCallback(async () => {
    setIsNotificationsLoading(true);
    setNotificationsError('');
    try {
      const data = await notificationService.getMine();
      setNotifications(
        [...data].sort((left, right) => {
          const leftTime = left.createdAt ? new Date(left.createdAt).getTime() : 0;
          const rightTime = right.createdAt ? new Date(right.createdAt).getTime() : 0;
          return rightTime - leftTime;
        }),
      );
    } catch (error: unknown) {
      setNotificationsError(
        error instanceof Error
          ? error.message
          : t('header.notifications.loadError', { defaultValue: 'Unable to load notifications.' }),
      );
    } finally {
      setIsNotificationsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!currentUser) {
      return;
    }

    void loadNotifications();
    const timer = window.setInterval(() => void loadNotifications(), 45000);
    return () => window.clearInterval(timer);
  }, [currentUser, loadNotifications]);

  const unreadNotificationsCount = useMemo(
    () => notifications.filter((notification) => notification.isOpenned === false).length,
    [notifications],
  );

  const openNotification = useCallback(
    async (notificationId: number) => {
      try {
        await notificationService.getOne(notificationId);
        await loadNotifications();
      } catch (error: unknown) {
        setNotificationsError(
          error instanceof Error
            ? error.message
            : t('header.notifications.openError', { defaultValue: 'Unable to open notification.' }),
        );
      }
    },
    [loadNotifications, t],
  );

  const deleteNotification = useCallback(
    async (notificationId: number) => {
      try {
        await notificationService.delete(notificationId);
        await loadNotifications();
      } catch (error: unknown) {
        setNotificationsError(
          error instanceof Error
            ? error.message
            : t('header.notifications.deleteError', { defaultValue: 'Unable to delete notification.' }),
        );
      }
    },
    [loadNotifications, t],
  );

  let navigationItems: NavItem[] = [
    { to: '/', labelKey: 'nav.home' },
    { to: '/about', labelKey: 'nav.about' },
    { to: '/publishers', labelKey: 'nav.publishers' },
    { to: '/books', labelKey: 'nav.products' },
    { to: '/pod', labelKey: 'nav.print' },
    { to: '/animation', labelKey: 'nav.animations' },
    { to: '/audio', labelKey: 'nav.audio' },
    { to: '/contact', labelKey: 'nav.contact' },
  ];

  if (currentUser?.role === 'buyer') {
    navigationItems = [
      { to: '/', labelKey: 'nav.home' },
      { to: '/about', labelKey: 'nav.about' },
      { to: '/publishers', labelKey: 'nav.publishers' },
      { to: '/books', labelKey: 'nav.products' },
      { to: '/pod', labelKey: 'nav.print' },
      { to: '/animation', labelKey: 'nav.animations' },
      { to: '/audio', labelKey: 'nav.audio' },
      { to: '/contact', labelKey: 'nav.contact' },
    ];
  }

  if (currentUser?.role === 'admin') {
    navigationItems = [
      { to: '/', labelKey: 'nav.home' },
      { to: '/about', labelKey: 'nav.about' },
      { to: '/publishers', labelKey: 'nav.publishers' },
      { to: '/books', labelKey: 'nav.products' },
      { to: '/pod', labelKey: 'nav.print' },
      { to: '/animation', labelKey: 'nav.animations' },
      { to: '/audio', labelKey: 'nav.audio' },
      { to: '/contact', labelKey: 'nav.contact' },
    ];
  }

  if (currentUser?.role === 'vendor') {
    navigationItems = [
      { to: '/', labelKey: 'nav.home' },
      { to: '/about', labelKey: 'nav.about' },
      { to: '/publishers', labelKey: 'nav.publishers' },
      { to: '/books', labelKey: 'nav.products' },
      { to: '/pod', labelKey: 'nav.print' },
      { to: '/animation', labelKey: 'nav.animations' },
      { to: '/audio', labelKey: 'nav.audio' },
      { to: '/contact', labelKey: 'nav.contact' },
    ];
  }

  const desktopNavClassName = (isActive: boolean): string =>
    `rounded-xl px-1.5 py-2 text-sm font-semibold whitespace-nowrap transition ${
      isActive ? 'bg-orange-100 text-orange-700' : 'text-slate-700 hover:bg-orange-50 hover:text-orange-600'
    }`;

  const mobileNavClassName = (isActive: boolean): string =>
    `rounded-xl px-2.5 py-2 text-sm font-semibold whitespace-nowrap transition ${
      isActive ? 'bg-orange-100 text-orange-700' : 'text-slate-700 hover:bg-orange-50 hover:text-orange-600'
    }`;

  const mobileMenuNavClassName = (isActive: boolean): string =>
    `flex min-h-[64px] flex-col justify-between rounded-[22px] border px-3 py-3 text-left transition ${
      isActive
        ? 'border-orange-200 bg-orange-50 text-orange-700 shadow-[0_14px_30px_-24px_rgba(249,115,22,0.7)]'
        : 'border-slate-200 bg-white text-slate-700 hover:border-orange-200 hover:bg-orange-50/60 hover:text-orange-600'
    }`;

  const utilityClass =
    'rounded-xl border border-slate-200 bg-white px-2.5 py-2 text-sm font-semibold text-orange-600 transition hover:border-orange-300 hover:text-orange-700';
  const cartButtonClass =
    'rounded-xl border border-slate-200 bg-white px-2 py-1.5 text-sm font-semibold text-orange-600 transition hover:border-orange-300 hover:text-orange-700';
  const mobileUtilityClass = 'rounded-xl border border-slate-200 bg-white p-2 text-slate-700';
  const mobileCartButtonClass = 'rounded-xl border border-slate-200 bg-white p-1.5 text-slate-700';
  const mobileLanguageSelectClass = 'w-[82px] rounded-xl border border-slate-200 bg-white px-2 py-2 text-[11px] font-semibold text-slate-700 focus:border-orange-400 focus:outline-none [&>option]:bg-white [&>option]:text-slate-900';
  const profileCardClass = 'rounded-xl border border-orange-100 bg-white p-1 text-slate-700';
  const profileMetaClass = 'text-slate-500';

  const mobileDockItems = useMemo(
    () => [
      {
        to: '/',
        label: t('nav.home', { defaultValue: 'Home' }),
        icon: Home,
        isActive: location.pathname === '/',
      },
      {
        to: '/books',
        label: t('nav.products', { defaultValue: 'Books' }),
        icon: BookOpen,
        isActive: location.pathname === '/books' || location.pathname.startsWith('/products'),
      },
      {
        to: '/pod',
        label: t('nav.print', { defaultValue: 'Print' }),
        icon: Printer,
        isActive: location.pathname === '/pod' || location.pathname.startsWith('/pod/'),
      },
      {
        to: '/animation',
        label: t('nav.animations', { defaultValue: 'Animation' }),
        icon: Film,
        isActive: location.pathname === '/animation' || location.pathname.startsWith('/animation/'),
      },
      {
        to: currentUser ? dashboardPath ?? '/profile' : '/auth',
        label: currentUser ? t(dashboardLabelKey, { defaultValue: 'Dashboard' }) : t('nav.login', { defaultValue: 'Login' }),
        icon: User,
        isActive: currentUser
          ? location.pathname === '/profile' ||
            location.pathname.startsWith('/dashboard') ||
            location.pathname.startsWith('/admin/dashboard') ||
            location.pathname.startsWith('/vendor/dashboard')
          : location.pathname === '/auth',
      },
    ],
    [currentUser, dashboardLabelKey, dashboardPath, location.pathname, t],
  );

  return (
    <header
      className={
        isHomePage
          ? 'fixed inset-x-0 top-0 z-40 bg-white shadow-[0_1px_12px_rgba(15,23,42,0.06)]'
          : 'fixed inset-x-0 top-0 z-40 bg-white shadow-[0_1px_12px_rgba(15,23,42,0.06)]'
      }
    >
      <div className="mx-auto w-full max-w-screen-2xl px-2.5 py-2.5 sm:px-4 xl:px-6">
        <div className="hidden xl:flex xl:items-center xl:justify-between xl:gap-3 xl:flex-nowrap">
          <Link to="/" className="flex min-w-0 items-center gap-2 sm:gap-2.5 flex-shrink-0">
            <img
              src="/Screenshot_2026-02-18_120944-removebg-preview.png"
              alt={t('common.brand')}
              className="h-10 w-10 rounded-lg object-contain sm:h-11 sm:w-11"
            />
            <div className="min-w-0">
              <p className="truncate text-lg font-bold text-slate-900 sm:text-xl">
                {t('common.brand', { defaultValue: 'Haapa Store' })}
              </p>
            </div>
          </Link>

          <div className="flex flex-1 items-center justify-center gap-1.5 flex-nowrap overflow-x-auto px-2">
            {navigationItems.map((navigationItem) => (
              <NavLink
                key={navigationItem.to}
                to={navigationItem.to}
                className={({ isActive }) => desktopNavClassName(isActive)}
              >
                {t(navigationItem.labelKey)}
              </NavLink>
            ))}
          </div>

          <div className="flex items-center gap-2 flex-nowrap flex-shrink-0">
            <button type="button" onClick={onOpenCart} className={`${cartButtonClass} relative`} aria-label={t('nav.cart')}>
              <ShoppingCart className="h-3.5 w-3.5" />
              {cartTotalItems > 0 && (
                <span className="absolute -right-1.5 -top-1.5 min-w-5 rounded-full bg-orange-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                  {cartTotalItems}
                </span>
              )}
            </button>

            <div className="relative" ref={desktopLanguageMenuRef}>
              <button
                type="button"
                onClick={() => setIsLanguageMenuOpen((value) => !value)}
                disabled={isLanguageLoading}
                className={`${utilityClass} relative inline-flex h-10 w-10 items-center justify-center p-0`}
                aria-label={t('language.label')}
                aria-expanded={isLanguageMenuOpen}
                aria-haspopup="menu"
                title={t(currentLanguageOption.labelKey)}
              >
                <Globe className="h-4 w-4" />
                <span className="absolute -bottom-1 -right-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full border border-white bg-white px-1 text-[11px] shadow-sm">
                  {getLanguageFlag(currentLanguageOption.code)}
                </span>
              </button>

              {isLanguageMenuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 z-50 mt-2 w-56 overflow-hidden rounded-2xl border border-orange-100 bg-white p-1.5 shadow-[0_18px_45px_-24px_rgba(15,23,42,0.28)]"
                >
                  {staticLanguageOptions.map((option) => {
                    const isActiveLanguage = option.code === currentLanguageOption.code;
                    return (
                      <button
                        key={option.code}
                        type="button"
                        onClick={() => void handleLanguageSelect(option.code)}
                        className={`flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-sm transition ${
                          isActiveLanguage ? 'bg-orange-50 text-orange-700' : 'text-slate-700 hover:bg-orange-50 hover:text-orange-700'
                        }`}
                      >
                        <span className="flex items-center gap-3">
                          <span className="text-base leading-none">{getLanguageFlag(option.code)}</span>
                          <span className="font-semibold">{t(option.labelKey)}</span>
                        </span>
                        {isActiveLanguage ? <Check className="h-4 w-4" /> : null}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={toggleTheme}
              className={`${utilityClass} inline-flex items-center justify-center p-2`}
              aria-label={t('header.theme.toggleAria', { defaultValue: 'Toggle theme' })}
              title={
                themeMode === 'dark'
                  ? t('header.theme.switchToLight', { defaultValue: 'Switch to light mode' })
                  : t('header.theme.switchToDark', { defaultValue: 'Switch to dark mode' })
              }
            >
              {themeMode === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>

            <button
              type="button"
              onClick={() => {
                setIsNotificationsOpen((value) => {
                  const next = !value;
                  if (next) {
                    void loadNotifications();
                  }
                  return next;
                });
              }}
              className={`${utilityClass} relative inline-flex items-center justify-center p-2`}
              aria-label={t('header.notifications.title', { defaultValue: 'Notifications' })}
              title={t('header.notifications.title', { defaultValue: 'Notifications' })}
            >
              <Bell className="h-4 w-4" />
              {unreadNotificationsCount > 0 && (
                <span className="absolute -right-1 -top-1 inline-flex min-w-4 items-center justify-center rounded-full bg-orange-500 px-1 text-[10px] font-bold leading-4 text-white">
                  {Math.min(99, unreadNotificationsCount)}
                </span>
              )}
            </button>
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            <div className="hidden items-center gap-2 xl:flex">
              {currentUser && (
                <div className="relative" ref={profileMenuRef}>
                  <button
                    type="button"
                    onClick={() => setIsProfileMenuOpen((isOpen) => !isOpen)}
                    className={`flex items-center gap-2 ${profileCardClass}`}
                    aria-expanded={isProfileMenuOpen}
                    aria-haspopup="menu"
                    aria-label="Profile menu"
                  >
                    {currentUser.profileImageUrl ? (
                      <img
                        src={currentUser.profileImageUrl}
                        alt={currentUser.name}
                        className="h-8 w-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-500 text-xs font-semibold text-white">
                        {getUserInitials(currentUser.name)}
                      </div>
                    )}
                    <div className="pr-2 text-left leading-tight">
                      <p className="text-xs font-semibold">{currentUser.name}</p>
                      <p className={`text-[10px] ${profileMetaClass}`}>{currentUser.country ?? currentUser.role.toUpperCase()}</p>
                    </div>
                  </button>

                  {isProfileMenuOpen && (
                    <div
                      role="menu"
                      className="absolute right-0 z-50 mt-2 w-44 rounded-xl border border-orange-100 bg-white p-1.5 shadow-lg"
                    >
                      {dashboardPath && (
                        <button
                          type="button"
                          onClick={handleOpenDashboard}
                          className="w-full rounded-lg px-3 py-2 text-left text-sm font-semibold text-slate-700 transition hover:bg-orange-50 hover:text-orange-700"
                        >
                          {t(dashboardLabelKey)}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={handleOpenProfile}
                        className="w-full rounded-lg px-3 py-2 text-left text-sm font-semibold text-slate-700 transition hover:bg-orange-50 hover:text-orange-700"
                      >
                        Edit Profile
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleLogout()}
                        disabled={isLoggingOut || isLoading}
                        className="w-full rounded-lg px-3 py-2 text-left text-sm font-semibold text-slate-700 transition hover:bg-orange-50 hover:text-orange-700"
                      >
                        {t('common.logout')}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {!currentUser && (
                <NavLink to="/auth" className={({ isActive }) => desktopNavClassName(isActive)}>
                  {t('nav.login')}
                </NavLink>
              )}
            </div>

            <div className="flex items-center gap-1 xl:hidden">
              <button type="button" onClick={onOpenCart} className={`${mobileCartButtonClass} relative`} aria-label={t('nav.cart')}>
                <ShoppingCart className="h-3.5 w-3.5" />
                {cartTotalItems > 0 && (
                  <span className="absolute -right-1 -top-1 min-w-4 rounded-full bg-orange-500 px-1 text-[9px] font-bold text-white">
                    {cartTotalItems}
                  </span>
                )}
              </button>
              <label className="relative">
                <span className="sr-only">{t('language.label')}</span>
                <select
                  value={currentLanguage}
                  onChange={(event) => void changeLanguage(event.target.value)}
                  disabled={isLanguageLoading}
                  className={mobileLanguageSelectClass}
                >
                  {staticLanguageOptions.map((option) => (
                    <option key={option.code} value={option.code} className="bg-white text-slate-900">
                      {option.shortLabel}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                onClick={toggleTheme}
                className={mobileUtilityClass}
                aria-label={t('header.theme.toggleAria', { defaultValue: 'Toggle theme' })}
                title={
                  themeMode === 'dark'
                    ? t('header.theme.switchToLight', { defaultValue: 'Switch to light mode' })
                    : t('header.theme.switchToDark', { defaultValue: 'Switch to dark mode' })
                }
              >
                {themeMode === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsNotificationsOpen((value) => {
                    const next = !value;
                    if (next) {
                      void loadNotifications();
                    }
                    return next;
                  });
                }}
                className={`${mobileUtilityClass} relative`}
                aria-label={t('header.notifications.title', { defaultValue: 'Notifications' })}
                title={t('header.notifications.title', { defaultValue: 'Notifications' })}
              >
                <Bell className="h-4 w-4" />
                {unreadNotificationsCount > 0 && (
                  <span className="absolute -right-1 -top-1 inline-flex min-w-4 items-center justify-center rounded-full bg-orange-500 px-1 text-[10px] font-bold leading-4 text-white">
                    {Math.min(99, unreadNotificationsCount)}
                  </span>
                )}
              </button>
              <button
                type="button"
                className={`${mobileUtilityClass} transition`}
                onClick={() => setMenuOpen((isOpen) => !isOpen)}
                aria-expanded={menuOpen}
                aria-controls="mobile-nav"
                aria-label={menuOpen ? t('common.close') : t('nav.menu')}
              >
                {menuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>

          <div className="flex flex-col gap-2.5 xl:hidden">
          <div className="flex items-center justify-between gap-3 rounded-[26px] border border-orange-100/80 bg-white/95 px-3.5 py-3 shadow-[0_18px_50px_-36px_rgba(15,23,42,0.38)] backdrop-blur-xl">
            <Link to="/" className="flex min-w-0 items-center gap-3">
              <img
                src="/Screenshot_2026-02-18_120944-removebg-preview.png"
                alt={t('common.brand')}
                className="h-11 w-11 rounded-2xl border border-orange-100 bg-orange-50 object-contain p-1"
              />
              <div className="min-w-0">
                <p className="truncate text-base font-extrabold text-slate-900">
                  {t('common.brand', { defaultValue: 'Haapa Store' })}
                </p>
                <p className="truncate text-[11px] font-semibold uppercase tracking-[0.16em] text-orange-600">
                  Books, POD, audio and more
                </p>
              </div>
            </Link>

            <div className="flex items-center gap-1.5">
              <button type="button" onClick={onOpenCart} className={`${mobileCartButtonClass} relative rounded-2xl p-2.5`} aria-label={t('nav.cart')}>
                <ShoppingCart className="h-4 w-4" />
                {cartTotalItems > 0 && (
                  <span className="absolute -right-1 -top-1 min-w-4 rounded-full bg-orange-500 px-1 text-[9px] font-bold text-white">
                    {cartTotalItems}
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsNotificationsOpen((value) => {
                    const next = !value;
                    if (next) {
                      void loadNotifications();
                    }
                    return next;
                  });
                }}
                className={`${mobileUtilityClass} relative rounded-2xl p-2.5`}
                aria-label={t('header.notifications.title', { defaultValue: 'Notifications' })}
                title={t('header.notifications.title', { defaultValue: 'Notifications' })}
              >
                <Bell className="h-4 w-4" />
                {unreadNotificationsCount > 0 && (
                  <span className="absolute -right-1 -top-1 inline-flex min-w-4 items-center justify-center rounded-full bg-orange-500 px-1 text-[10px] font-bold leading-4 text-white">
                    {Math.min(99, unreadNotificationsCount)}
                  </span>
                )}
              </button>
              <button
                type="button"
                className={`${mobileUtilityClass} rounded-2xl p-2.5 transition`}
                onClick={() => setMenuOpen((isOpen) => !isOpen)}
                aria-expanded={menuOpen}
                aria-controls="mobile-nav"
                aria-label={menuOpen ? t('common.close') : t('nav.menu')}
              >
                {menuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="relative" ref={mobileLanguageMenuRef}>
              <button
                type="button"
                onClick={() => setIsLanguageMenuOpen((value) => !value)}
                disabled={isLanguageLoading}
                className="relative inline-flex items-center justify-center rounded-[22px] border border-orange-100 bg-white px-4 py-3 text-slate-700 shadow-[0_16px_35px_-30px_rgba(15,23,42,0.35)] transition hover:border-orange-300 hover:text-orange-600"
                aria-label={t('language.label')}
                aria-expanded={isLanguageMenuOpen}
                aria-haspopup="menu"
                title={t(currentLanguageOption.labelKey)}
              >
                <Globe className="h-4 w-4" />
                <span className="absolute -bottom-1 -right-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full border border-white bg-white px-1 text-[11px] shadow-sm">
                  {getLanguageFlag(currentLanguageOption.code)}
                </span>
              </button>

              {isLanguageMenuOpen && (
                <div
                  role="menu"
                  className="absolute left-0 z-50 mt-2 w-56 overflow-hidden rounded-2xl border border-orange-100 bg-white p-1.5 shadow-[0_18px_45px_-24px_rgba(15,23,42,0.28)] sm:left-auto sm:right-0"
                >
                  {staticLanguageOptions.map((option) => {
                    const isActiveLanguage = option.code === currentLanguageOption.code;
                    return (
                      <button
                        key={option.code}
                        type="button"
                        onClick={() => void handleLanguageSelect(option.code)}
                        className={`flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-sm transition ${
                          isActiveLanguage ? 'bg-orange-50 text-orange-700' : 'text-slate-700 hover:bg-orange-50 hover:text-orange-700'
                        }`}
                      >
                        <span className="flex items-center gap-3">
                          <span className="text-base leading-none">{getLanguageFlag(option.code)}</span>
                          <span className="font-semibold">{t(option.labelKey)}</span>
                        </span>
                        {isActiveLanguage ? <Check className="h-4 w-4" /> : null}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={toggleTheme}
              className="inline-flex items-center justify-center rounded-[22px] border border-orange-100 bg-white px-4 py-3 text-slate-700 shadow-[0_16px_35px_-30px_rgba(15,23,42,0.35)] transition hover:border-orange-300 hover:text-orange-600"
              aria-label={t('header.theme.toggleAria', { defaultValue: 'Toggle theme' })}
              title={
                themeMode === 'dark'
                  ? t('header.theme.switchToLight', { defaultValue: 'Switch to light mode' })
                  : t('header.theme.switchToDark', { defaultValue: 'Switch to dark mode' })
              }
            >
              {themeMode === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>

            <button
              type="button"
              onClick={() => {
                setIsNotificationsOpen((value) => {
                  const next = !value;
                  if (next) {
                    void loadNotifications();
                  }
                  return next;
                });
              }}
              className="inline-flex items-center justify-center rounded-[22px] border border-orange-100 bg-white px-4 py-3 text-slate-700 shadow-[0_16px_35px_-30px_rgba(15,23,42,0.35)] transition hover:border-orange-300 hover:text-orange-600"
              aria-label={t('header.notifications.title', { defaultValue: 'Notifications' })}
              title={t('header.notifications.title', { defaultValue: 'Notifications' })}
            >
              <Bell className="h-4 w-4" />
              {unreadNotificationsCount > 0 && (
                <span className="ml-1 inline-flex min-w-4 items-center justify-center rounded-full bg-orange-500 px-1 text-[10px] font-bold leading-4 text-white">
                  {Math.min(99, unreadNotificationsCount)}
                </span>
              )}
            </button>

            {currentUser ? (
              <button
                type="button"
                onClick={() => void navigate('/profile')}
                className="inline-flex items-center justify-center rounded-[22px] border border-orange-100 bg-white px-4 py-3 text-slate-700 shadow-[0_16px_35px_-30px_rgba(15,23,42,0.35)] transition hover:border-orange-300 hover:text-orange-600"
                aria-label="Open profile"
              >
                {currentUser.profileImageUrl ? (
                  <img src={currentUser.profileImageUrl} alt={currentUser.name} className="h-5 w-5 rounded-full object-cover" />
                ) : (
                  <User className="h-4 w-4" />
                )}
              </button>
            ) : (
              <NavLink
                to="/auth"
                className="inline-flex items-center justify-center rounded-[22px] border border-orange-100 bg-white px-4 py-3 text-xs font-semibold text-slate-700 shadow-[0_16px_35px_-30px_rgba(15,23,42,0.35)] transition hover:border-orange-300 hover:text-orange-600"
              >
                {t('nav.login', { defaultValue: 'Login' })}
              </NavLink>
            )}
          </div>
        </div>

        <nav
          id="mobile-nav"
            className={`mt-2 max-h-[72vh] overflow-y-auto rounded-[28px] border border-orange-100/80 bg-white/95 p-3 shadow-[0_28px_60px_-38px_rgba(15,23,42,0.38)] backdrop-blur xl:hidden ${
              menuOpen ? 'block' : 'hidden'
            }`}
          >
          <div className="mb-3 rounded-[24px] bg-[linear-gradient(135deg,#fff7ed_0%,#ffffff_52%,#fff1e8_100%)] px-4 py-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-orange-600">Mobile Menu</p>
            <p className="mt-1 text-sm font-semibold text-slate-900">Quick access to the full Haapa experience.</p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            {navigationItems.map((navigationItem) => (
              <NavLink
                key={navigationItem.to}
                to={navigationItem.to}
                className={({ isActive }) => mobileMenuNavClassName(isActive)}
                onClick={() => setMenuOpen(false)}
              >
                <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-orange-500">Navigate</span>
                <span className="mt-1 text-sm font-semibold">{t(navigationItem.labelKey)}</span>
              </NavLink>
            ))}
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                onOpenCart();
              }}
              className="rounded-[22px] border border-slate-200 bg-white px-3 py-3 text-left text-sm font-semibold text-slate-700 transition hover:border-orange-200 hover:bg-orange-50/60 hover:text-orange-600"
            >
              {t('nav.cart')} ({cartTotalItems})
            </button>
            <button
              type="button"
              onClick={toggleTheme}
              className="rounded-[22px] border border-slate-200 bg-white px-3 py-3 text-left text-sm font-semibold text-slate-700 transition hover:border-orange-200 hover:bg-orange-50/60 hover:text-orange-600"
            >
              {themeMode === 'dark' ? 'Switch to Light Mode' : 'Switch to Night Mode'}
            </button>
          </div>

          {currentUser && (
            <div className="mt-3 rounded-[24px] border border-orange-100 bg-orange-50/70 p-3">
              <button
                type="button"
                onClick={() => setIsProfileMenuOpen((isOpen) => !isOpen)}
                className="flex w-full items-center gap-3 text-left"
                aria-expanded={isProfileMenuOpen}
                aria-haspopup="menu"
              >
                {currentUser.profileImageUrl ? (
                  <img
                    src={currentUser.profileImageUrl}
                    alt={currentUser.name}
                    className="h-8 w-8 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-500 text-xs font-semibold text-white">
                    {getUserInitials(currentUser.name)}
                  </div>
                )}
                <div className="leading-tight">
                  <p className="text-sm font-semibold text-slate-700">{currentUser.name}</p>
                  <p className="text-[11px] text-slate-500">{currentUser.country ?? currentUser.role.toUpperCase()}</p>
                </div>
              </button>
              <div className={`mt-2 grid gap-1 ${isProfileMenuOpen ? 'block' : 'hidden'}`}>
                {dashboardPath && (
                  <button
                    type="button"
                    onClick={handleOpenDashboard}
                    className="w-full rounded-lg bg-white px-3 py-2 text-left text-xs font-semibold text-slate-700 transition hover:bg-orange-100 hover:text-orange-700"
                  >
                    {t(dashboardLabelKey)}
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleOpenProfile}
                  className="w-full rounded-lg bg-white px-3 py-2 text-left text-xs font-semibold text-slate-700 transition hover:bg-orange-100 hover:text-orange-700"
                >
                  Edit Profile
                </button>
                <button
                  type="button"
                  onClick={() => void handleLogout()}
                  disabled={isLoggingOut || isLoading}
                  className="w-full rounded-lg bg-white px-3 py-2 text-left text-xs font-semibold text-slate-700 transition hover:bg-orange-100 hover:text-orange-700"
                >
                  {t('common.logout')}
                </button>
              </div>
            </div>
          )}

          {!currentUser && (
            <NavLink to="/auth" className={({ isActive }) => mobileNavClassName(isActive)} onClick={() => setMenuOpen(false)}>
              {t('nav.login')}
            </NavLink>
          )}

        </nav>
      </div>

          <div className="fixed inset-x-0 bottom-4 z-[70] px-3 sm:px-4 xl:hidden">
        <div className="mx-auto w-full max-w-md rounded-[30px] border border-orange-100/90 bg-white/95 p-2 shadow-[0_26px_70px_-40px_rgba(15,23,42,0.42)] backdrop-blur-xl md:max-w-3xl md:px-3 md:py-2.5">
          <div className="flex gap-1.5 overflow-x-auto pb-1 scroll-smooth snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:grid md:grid-cols-5 md:gap-2 md:overflow-visible md:pb-0">
            {mobileDockItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={`${item.to}-${item.label}`}
                  to={item.to}
                  onClick={() => setMenuOpen(false)}
                  className={`flex min-w-[78px] snap-start flex-col items-center justify-center gap-1 rounded-[22px] px-2 py-2.5 text-center transition sm:min-w-[84px] md:min-w-0 md:px-3 md:py-3 ${
                    item.isActive
                      ? 'bg-orange-500 text-white shadow-[0_18px_35px_-22px_rgba(249,115,22,0.9)]'
                      : 'text-slate-500 hover:bg-orange-50 hover:text-orange-600'
                  }`}
                >
                  <Icon className="h-[18px] w-[18px] shrink-0 md:h-5 md:w-5" />
                  <span className="line-clamp-1 text-[10px] font-semibold leading-tight sm:text-[11px] md:text-xs">{item.label}</span>
                </NavLink>
              );
            })}
          </div>
          <div className="mx-auto mt-2 h-1 w-16 rounded-full bg-slate-200 md:hidden" />
        </div>
      </div>

      {isNotificationsOpen && (
        <div className="fixed inset-0 z-[60]">
          <button
            type="button"
            className="absolute inset-0 bg-slate-950/15"
            aria-label="Close notifications"
            onClick={() => setIsNotificationsOpen(false)}
          />
          <aside className="absolute right-2 top-[72px] w-[380px] max-w-[calc(100vw-16px)] overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl sm:right-4">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  {t('header.notifications.title', { defaultValue: 'Notifications' })}
                </p>
                <p className="mt-0.5 text-xs text-slate-500">
                  {t('header.notifications.subtitle', { defaultValue: 'Messages sent by the admin.' })}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {currentUser && unreadNotificationsCount > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      const unread = notifications.filter((notification) => notification.isOpenned === false);
                      void (async () => {
                        for (const notification of unread) {
                          await openNotification(notification.id);
                        }
                      })();
                    }}
                    className="rounded-full border border-slate-200 bg-white px-3 py-2 text-[11px] font-semibold text-slate-700 transition hover:bg-slate-50"
                    title={t('header.notifications.markAllRead', { defaultValue: 'Mark all as read' })}
                  >
                    {t('header.notifications.markRead', { defaultValue: 'Mark read' })}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setIsNotificationsOpen(false)}
                  className="rounded-full border border-slate-200 bg-white p-2 text-slate-700 transition hover:bg-slate-50"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="max-h-[70vh] overflow-y-auto p-3">
              {!currentUser ? (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                  {t('header.notifications.loginToView', { defaultValue: 'Login to view notifications.' })}
                </div>
              ) : isNotificationsLoading && notifications.length === 0 ? (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                  {t('common.loading', { defaultValue: 'Loading...' })}
                </div>
              ) : notificationsError ? (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
                  {notificationsError}
                </div>
              ) : notifications.length === 0 ? (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                  {t('header.notifications.none', { defaultValue: 'No notifications yet.' })}
                </div>
              ) : (
                <div className="space-y-2">
                  {notifications.slice(0, 30).map((notification) => (
                    <div
                      key={notification.id}
                      className={`rounded-2xl border px-4 py-3 ${
                        notification.isOpenned === false ? 'border-orange-200 bg-orange-50/40' : 'border-slate-200 bg-white'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => void openNotification(notification.id)}
                        className="w-full text-left"
                      >
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          {notification.isOpenned === false
                            ? t('header.notifications.statusNew', { defaultValue: 'New' })
                            : t('header.notifications.statusSeen', { defaultValue: 'Seen' })}
                        </p>
                        <p className="mt-1 line-clamp-3 text-sm font-semibold text-slate-900 whitespace-pre-wrap">
                          {notification.notificationBody || '-'}
                        </p>
                        {notification.createdAt && (
                          <p className="mt-2 text-[11px] font-semibold text-slate-400">
                            {new Date(notification.createdAt).toLocaleString()}
                          </p>
                        )}
                      </button>
                      <div className="mt-2 flex justify-end">
                        <button
                          type="button"
                          onClick={() => void deleteNotification(notification.id)}
                          className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-700 transition hover:bg-slate-50"
                        >
                          {t('common.delete', { defaultValue: 'Delete' })}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </aside>
        </div>
      )}
    </header>
  );
};

export default SiteHeader;
